// Note that many of the concerns handled by the corresponding BE module aren't relevant since loaders will
// load the GraphQL ASTs, and we don't insert __typename on the FE. (Apollo handles when it sends the queries
// to the server.)
import {gql} from '@apollo/client';
import {DocumentNode, FragmentDefinitionNode, FragmentSpreadNode, visit} from 'graphql';
import _ from 'underscore';

import {assert} from 'Asserts';
import {getFragDefFromDoc} from 'graphql-client/AST';
import lazyInit from 'util/lazyInit';

import {ALL_FRAGMENTS_AND_DECLARATIONS, FragmentAndDeclaration} from './AllFragments';
import {RESTSelectionSetOptions} from './Types';

/** Returns the declaration name for a GraphQL fragment.
 *
 * e.g. BioentitySummaryGraphQL => BioentitySummary
 */
function getDeclarationName(fragmentName: string): string {
  assert(
    fragmentName.endsWith('GraphQL'),
    `Invalid fragment name, expected 'GraphQL' suffix, got ${fragmentName}`
  );
  return fragmentName.replace('GraphQL', '');
}

// Exported for tests
export function findDependentFragmentNames(fragmentDefinitionNode: FragmentDefinitionNode): Set<string> {
  const fragmentNames = new Set<string>();
  visit(fragmentDefinitionNode, {
    FragmentSpread(node: FragmentSpreadNode): unknown {
      fragmentNames.add(node.name.value);
      return false;
    },
  });
  return fragmentNames;
}

class DependencyMap {
  fragNameToDependentNames: {[fragName: string]: Set<string>};

  constructor(fragNameToDependentNames: {[fragName: string]: Set<string>}) {
    this.fragNameToDependentNames = fragNameToDependentNames;
  }

  public static create(fragDefNodes: Array<FragmentDefinitionNode>): DependencyMap {
    return new DependencyMap(
      // eslint-disable-next-line benchlint/no-spread-reduce
      fragDefNodes.reduce(
        (memo, fragDef) => ({
          ...memo,
          [fragDef.name.value]: findDependentFragmentNames(fragDef),
        }),
        {}
      )
    );
  }

  public getAllDependentFragmentNames(fragmentName: string): Array<string> {
    assert(
      this.fragNameToDependentNames.hasOwnProperty(fragmentName),
      `Could not find definition for fragment ${fragmentName} (did you remember to generate artifacts?)`
    );
    const ownDeps = this.fragNameToDependentNames[fragmentName];
    const recursiveDeps = [...ownDeps].flatMap((dep) => this.getAllDependentFragmentNames(dep));
    return Array.from(new Set([...ownDeps, ...recursiveDeps]));
  }
}

// Exported for tests
export interface Mapping {
  [title: string]: FragmentAndDeclaration;
}

export class DeclarativeTranslationsMapping {
  private declarationNameToFragmentAndDeclaration: Mapping;
  private fragmentDepenedencyMap: DependencyMap;
  private cachedDeclarationNameToDocWithDependencies: {[declName: string]: DocumentNode};

  private static validateMapping(mapping: Mapping): void {
    for (const fragmentAndDeclaration of Object.values(mapping)) {
      const fragment = getFragDefFromDoc(fragmentAndDeclaration.graphQLDoc);
      const gqlName = fragment.name.value;
      assert(
        getDeclarationName(gqlName) === fragmentAndDeclaration.title,
        `Invalid fragment name, expected to match declaration name with translation, got ${gqlName}`
      );
    }
  }

  constructor(mapping: Mapping, dependencyMap: DependencyMap) {
    DeclarativeTranslationsMapping.validateMapping(mapping);
    this.declarationNameToFragmentAndDeclaration = mapping;
    this.fragmentDepenedencyMap = dependencyMap;

    // One route would have been to construct DocumentNode objects on the fly using the
    // FragmentDefinitionNodes we loaded. This ends up not working because graphql-tag dumps the string source
    // (which requires DocumentNode.loc.source). We could construct that but it ends up somewhat hacky - the
    // AST requires tokens, etc. It's simpler to just feed the fragment definitions to gql() and cache the
    // result for performance.
    this.cachedDeclarationNameToDocWithDependencies = {};
  }

  // Public for tests
  public static createFromMapping(mapping: Mapping): DeclarativeTranslationsMapping {
    const dependencyMap = DependencyMap.create(
      Object.values(mapping).map((decl) => decl.graphQLDoc.definitions[0] as FragmentDefinitionNode)
    );
    return new DeclarativeTranslationsMapping(mapping, dependencyMap);
  }

  public static loadAndCreate(): DeclarativeTranslationsMapping {
    return DeclarativeTranslationsMapping.createFromMapping(
      _.indexBy(ALL_FRAGMENTS_AND_DECLARATIONS, (fragAndDecl) => fragAndDecl.title)
    );
  }

  private getFragmentDoc(fragmentName: string): DocumentNode {
    return this.declarationNameToFragmentAndDeclaration[getDeclarationName(fragmentName)].graphQLDoc;
  }

  public getParsedGraphQLFragmentFromName(fragmentName: string): FragmentDefinitionNode {
    return this.getFragmentDoc(fragmentName).definitions[0] as FragmentDefinitionNode;
  }

  public getDocumentWithDependenciesForFragment(declarationName: string): DocumentNode {
    if (this.cachedDeclarationNameToDocWithDependencies[declarationName]) {
      return this.cachedDeclarationNameToDocWithDependencies[declarationName];
    }

    const fragmentDoc = this.declarationNameToFragmentAndDeclaration[declarationName].graphQLDoc;
    const parsedFragment = fragmentDoc.definitions[0] as FragmentDefinitionNode;

    const subfragmentNames = this.fragmentDepenedencyMap.getAllDependentFragmentNames(
      parsedFragment.name.value
    );
    const subfragmentDocs = subfragmentNames.map((name) => this.getFragmentDoc(name));

    const compositeDoc = gql(
      [fragmentDoc, ...subfragmentDocs].map((fragment) => fragment.loc!.source.body).join('\n')
    );
    this.cachedDeclarationNameToDocWithDependencies[declarationName] = compositeDoc;
    return compositeDoc;
  }

  public getRESTDeclaration(name: string): RESTSelectionSetOptions {
    assert(
      this.declarationNameToFragmentAndDeclaration[name].restDeclaration != null,
      `Declarative translation called on incomplete declaration pairing with name: ${name}`
    );

    return this.declarationNameToFragmentAndDeclaration[name].restDeclaration!;
  }
}

export const getDeclarativeTranslationsMapping = lazyInit(() =>
  DeclarativeTranslationsMapping.loadAndCreate()
);
